import {
  POLLS_CLOSE_DATE,
  useCountdown,
  useTimeUntil,
} from '../lib/countdown';
import FeatureSection from './FeatureSection';
import HunchMainEmbed from './HunchEmbed/HunchMainEmbed';
import { renderEmphasis, useSectionText } from '../lib/useSectionText';
import { useElectionDayTurnout } from '../lib/useElectionDayTurnout';
import { hasTurnoutData } from '../data/electionday-turnout-types';
import { useElectionDayEditorial } from '../lib/useElectionDayEditorial';
import { useCandidates } from '../lib/useCandidates';
import {
  TURNOUT_2022_2PM_NATIONAL,
  TURNOUT_2022_CLOSE_NATIONAL,
} from '../data/turnoutHistory';
import type { ElectionPhase } from '../data/electionPhase-types';
import './Hero.css';

// Winner-announcement copy — minimal in Stage 3A (per spec, the
// prominent one-tap announce button is Stage 3D). The hero just
// READS the existing editorial nationalCall and surfaces it in the
// headline slot when party is set, across both results_pending and
// counting phases.
const PARTY_FULL_NAME: Record<'PL' | 'PN', string> = {
  PL: 'Partit Laburista',
  PN: 'Partit Nazzjonalista',
};

interface HeroExtra {
  badge?: string;
  meta?: { label: string; value: string }[];
  countdown_label?: string;
  countdown_date?: string;
  countdown_note?: string;
}

interface Props {
  /** Drives the polls_open variant. Defaults to 'normal' so existing
   *  callers (HomepageNormal) keep their current behaviour. */
  phase?: ElectionPhase;
}

// ── Normal-phase defaults (DB-backed via section_text 'hero' row) ──
const DEFAULT_BADGE = 'Election 2026';
const DEFAULT_TITLE = (
  <>
    Malta Votes <em>2026</em>
  </>
);
const DEFAULT_LEDE =
  "The most complete picture of Malta's general election. Updated continuously.";
const DEFAULT_META: { label: string; value: string }[] = [
  { label: 'Election Day', value: '30 May 2026' },
  { label: 'Districts', value: '13' },
  { label: 'Seats', value: '65' },
];
const DEFAULT_COUNTDOWN_LABEL = 'Days to Election';
const DEFAULT_COUNTDOWN_DATE = 'Sat 30 May 2026';
const DEFAULT_COUNTDOWN_NOTE = 'Polls open 07:00–22:00';

// ── hardcoded post-Stage-0 phase copy ──
// Not wired through section_text yet (same reason as before: would
// need the typed SectionKey widened + admin tabs + default JSX
// blocks). The election-day phases (polls_open, results_pending,
// counting) share the same headline as normal; framing comes from
// the badge + LIVE dot + lede + card.
const POLLS_OPEN_BADGE = 'LIVE';
const POLLS_OPEN_LEDE = 'The latest developments, as we get them.';

const RESULTS_PENDING_BADGE = 'LIVE';
const RESULTS_PENDING_LEDE = 'Live from the counting hall.';
const RESULTS_PENDING_CARD_LABEL = 'Vote sorting underway';
const RESULTS_PENDING_CARD_SECONDARY = 'Naxxar Counting Hall';

const COUNTING_BADGE = 'Counting';
// Matches RESULTS_PENDING_LEDE so the hero reads with the same
// editorial voice across the two live phases — editor doesn't want
// a different framing once counting actually starts; the badge +
// status card already carry the phase distinction.
const COUNTING_LEDE = 'Live from the counting hall.';
const COUNTING_CARD_LABEL = 'Counting';
const COUNTING_CARD_SECONDARY = 'Live updates as the count progresses.';

// Stage 1 — counting-sibling phases. SAME layout as counting; only
// the Hero copy varies. The added /official-results link for the
// `official_results` phase is rendered by the homepage composition
// wrapper (HomepageCountingBase in App.tsx), not by Hero itself.
const COUNTING_OVER_BADGE = 'Counting Concluded';
const COUNTING_OVER_LEDE =
  'Counting has concluded. Awaiting the Electoral Commission’s official confirmation.';
const COUNTING_OVER_CARD_LABEL = 'Counting Concluded';
const COUNTING_OVER_CARD_SECONDARY =
  'Awaiting the official commission release.';

const OFFICIAL_RESULTS_BADGE = 'OFFICIAL RESULTS';
const OFFICIAL_RESULTS_LEDE =
  'The Electoral Commission has published the official results.';
const OFFICIAL_RESULTS_CARD_LABEL = 'Official Results';
const OFFICIAL_RESULTS_CARD_SECONDARY =
  'See the full official-results page below.';

// Post-election archive copy. The election is over; the homepage is
// now a static record. No live framing, no LIVE dot — just a clean
// "Results" surface.
const POST_ELECTION_BADGE = 'Election 2026 · Results';
const POST_ELECTION_LEDE =
  'The official 2026 General Election results. Map, round-by-round counts, and elected representatives by district.';
const POST_ELECTION_CARD_LABEL = 'Counting concluded';
const POST_ELECTION_CARD_SECONDARY = '65 seats filled across 13 districts.';

const POST_CLOSE_CARD_DATE = 'Sat 30 May 2026';
const POST_CLOSE_CARD_NOTE = 'Polls closed at 22:00 local time';

export default function Hero({ phase = 'normal' }: Props) {
  // Phase shape flags. isAnyLive covers all post-pre-election phases
  // that share the live-styling primitives (LIVE dot in badge,
  // turnout headline eligibility, status-header card).
  // counting_over + official_results inherit the `counting` shape
  // (Stage 1 sibling phases — same composition, different Hero copy).
  const isPollsOpen = phase === 'polls_open';
  const isResultsPending = phase === 'results_pending';
  const isCounting = phase === 'counting';
  const isCountingOver = phase === 'counting_over';
  const isOfficialResults = phase === 'official_results';
  const isPostElection = phase === 'post_election';
  const isAnyCounting = isCounting || isCountingOver || isOfficialResults;
  // post_election sits alongside the counting siblings for "show
  // status pill + supporting line in the hero card" purposes, but is
  // deliberately NOT in isAnyLive — no LIVE dot, no turnout headline,
  // no winner-call precedence (the editorial nationalCall is allowed
  // to stay as a static record, but we read it the same way).
  const isPostClose = isResultsPending || isAnyCounting || isPostElection;
  const isAnyLive = isPollsOpen || isResultsPending || isAnyCounting;

  // Both timers run unconditionally — React hooks must be called in
  // a stable order across renders. Each is cheap (one setInterval).
  const daysToElection = useCountdown();
  const timeToClose = useTimeUntil(POLLS_CLOSE_DATE);

  const row = useSectionText('hero');
  const extra = (row?.extra ?? {}) as HeroExtra;

  // Editorial nationalCall — drives the winner announcement (Stage 3A
  // precedence rule). REAL DATA ONLY: this reads from the editorial
  // payload, never the demo dataset Stage 3A Part 4 introduces.
  const { data: editorial } = useElectionDayEditorial();

  // Candidate count for the polls_open meta row. Counts UNIQUE PEOPLE
  // running (one row per candidate from /api/candidates, with
  // district + district_2 merged), not total district-candidacies —
  // someone running in two districts is one candidate, not two.
  // The hook has a build-time fallback so this never renders 0
  // because of a cold cache or DB blip.
  const { candidates } = useCandidates();
  const candidateCount = candidates.length;
  const winnerParty = isPostClose ? editorial.nationalCall.party : null;
  const showWinnerAnnouncement = winnerParty !== null;

  // Turnout: fetched unconditionally (hooks rule) but only surfaced
  // in the headline slot in any live phase AND when the editor has
  // saved at least one national pct AND there's no winner announcement
  // taking precedence.
  const { data: turnout } = useElectionDayTurnout();
  const turnoutHeadlinePct = isAnyLive
    ? (turnout.national.pct_10pm ?? turnout.national.pct_2pm)
    : null;
  const showTurnoutHeadline =
    !showWinnerAnnouncement
    && isAnyLive
    && hasTurnoutData(turnout)
    && turnoutHeadlinePct !== null;
  const turnoutKicker = turnout.label ?? (turnout.national.pct_10pm !== null ? 'Final' : 'Live');

  // Phase-aware badge + lede. Title is the same in every phase
  // ("Malta Votes 2026" via section_text fallback) — the polls-
  // open / post-close framing comes from the surrounding chrome.
  let badge: string;
  if (isPollsOpen) badge = POLLS_OPEN_BADGE;
  else if (isPostElection) badge = POST_ELECTION_BADGE;
  else if (isResultsPending) badge = RESULTS_PENDING_BADGE;
  else if (isOfficialResults) badge = OFFICIAL_RESULTS_BADGE;
  else if (isCountingOver) badge = COUNTING_OVER_BADGE;
  else if (isCounting) badge = COUNTING_BADGE;
  else badge = extra.badge ?? DEFAULT_BADGE;

  const title = row?.title ? renderEmphasis(row.title) : DEFAULT_TITLE;

  let lede: string;
  if (isPollsOpen) lede = POLLS_OPEN_LEDE;
  else if (isPostElection) lede = POST_ELECTION_LEDE;
  else if (isResultsPending) lede = RESULTS_PENDING_LEDE;
  else if (isOfficialResults) lede = OFFICIAL_RESULTS_LEDE;
  else if (isCountingOver) lede = COUNTING_OVER_LEDE;
  else if (isCounting) lede = COUNTING_LEDE;
  else lede = row?.intro ?? DEFAULT_LEDE;

  // Meta tile row. polls_open swaps the middle tile (DISTRICTS) for
  // NUMBER OF CANDIDATES so the framing puts people on the ballot
  // front-and-centre; Election Day + Seats are unchanged. Other
  // phases keep the default meta (or the section_text override if
  // the editor has set one). Falls back to "—" while the candidate
  // hook is loading.
  let meta: { label: string; value: string }[];
  if (isPollsOpen) {
    meta = [
      { label: 'Election Day', value: '30 May 2026' },
      { label: 'Number of Candidates', value: candidateCount > 0 ? String(candidateCount) : '—' },
      { label: 'Seats', value: '65' },
    ];
  } else {
    meta =
      Array.isArray(extra.meta) && extra.meta.length > 0 ? extra.meta : DEFAULT_META;
  }

  // ── Card content ──────────────────────────────────────────────
  // normal     → big days-number to election (DB-overridable)
  // polls_open → POLLS OPEN status + time-until-close secondary
  // results_pending / counting → POLLS CLOSED / COUNTING status +
  //              short secondary text. Stage 3D may swap these for
  //              richer post-call states.
  const countdownLabel = extra.countdown_label ?? DEFAULT_COUNTDOWN_LABEL;
  const countdownNumber = daysToElection;
  const countdownUnit = 'days';
  // Date + note render only when !isPollsOpen (the polls_open card
  // folds both into its single "Closes 22:00" subtitle), so we only
  // need to resolve them for normal / post-close.
  const countdownDate = isPostClose
    ? POST_CLOSE_CARD_DATE
    : (extra.countdown_date ?? DEFAULT_COUNTDOWN_DATE);
  const countdownNote = isPostClose
    ? POST_CLOSE_CARD_NOTE
    : (extra.countdown_note ?? DEFAULT_COUNTDOWN_NOTE);

  // Polls-open countdown — single live timer, displayed prominently
  // in the card. We format as either "Hh MMm" (≥1 hr) or "MMm"
  // (<1 hr). The useTimeUntil hook re-renders each second so the
  // value is always current.
  let pollsOpenTimerPrimary = '';
  let pollsOpenTimerUnit = '';
  let pollsOpenExpired = false;
  if (isPollsOpen) {
    pollsOpenExpired = timeToClose.expired;
    if (!pollsOpenExpired) {
      const m = timeToClose.minutes;
      if (timeToClose.hours >= 1) {
        pollsOpenTimerPrimary = String(timeToClose.hours);
        pollsOpenTimerUnit = `h ${String(m).padStart(2, '0')}m`;
      } else {
        pollsOpenTimerPrimary = String(m);
        pollsOpenTimerUnit = 'min';
      }
    }
  }

  // Post-close status header copy.
  const postCloseStatusLabel = isPostElection
    ? POST_ELECTION_CARD_LABEL
    : isResultsPending
      ? RESULTS_PENDING_CARD_LABEL
      : isOfficialResults
        ? OFFICIAL_RESULTS_CARD_LABEL
        : isCountingOver
          ? COUNTING_OVER_CARD_LABEL
          : COUNTING_CARD_LABEL;
  const postCloseStatusSecondary = isPostElection
    ? POST_ELECTION_CARD_SECONDARY
    : isResultsPending
      ? RESULTS_PENDING_CARD_SECONDARY
      : isOfficialResults
        ? OFFICIAL_RESULTS_CARD_SECONDARY
        : isCountingOver
          ? COUNTING_OVER_CARD_SECONDARY
          : COUNTING_CARD_SECONDARY;

  return (
    <section
      className={`hero${isPollsOpen ? ' hero--polls-open' : ''}${isResultsPending ? ' hero--results-pending' : ''}${isCounting ? ' hero--counting' : ''}`}
      id="top"
    >
      <div className="hero__inner">
        <div className="hero__lead">
          <span
            className={`hero__badge${isAnyLive ? ' hero__badge--live' : ''}`}
          >
            {isAnyLive && (
              <span className="hero__live-dot" aria-hidden="true" />
            )}
            {badge}
          </span>
          <h1 className="hero__title">{title}</h1>
          <p className="hero__lede">{lede}</p>
          {/* Meta slot — Stage 3A precedence rule:
                1. Winner announcement (results_pending or counting,
                   editorial nationalCall.party set) — highest
                2. Turnout headline (any live phase, turnout entered)
                3. Default three meta tiles
              REAL-DATA-ONLY: the winner branch reads editorial,
              never the demo dataset. Demo mode can drive Stage 3C
              map/table visualisations but never this slot. */}
          {showWinnerAnnouncement && winnerParty ? (
            <div className={`hero__winner hero__winner--${winnerParty.toLowerCase()}`}>
              <div className="hero__winner-figure">
                <p className="hero__winner-kicker">
                  Result · {editorial.nationalCall.label || 'Called'}
                </p>
                <p className="hero__winner-party">
                  {PARTY_FULL_NAME[winnerParty]}
                </p>
                <p className="hero__winner-headline">wins.</p>
                {(() => {
                  // Margin of victory — three states, walked in order:
                  //   1. OFFICIAL: any of the *_official percentages set
                  //      → "Official margin · +X.X pp (winner% vs runner%)"
                  //      Strong styling (white chip).
                  //   2. PROJECTED: editor entered projected_margin_pct OR
                  //      projected_margin_votes from /admin/announce →
                  //      "Projected margin · +X.X pp" or "+X,XXX votes".
                  //      Softer yellow styling. If both are set, prefer pp.
                  //   3. NEITHER: render nothing. No subheading at all —
                  //      the editor hasn't given us a figure yet, and we
                  //      do NOT invent one from the (possibly stale)
                  //      per-party pl_pct/pn_pct fields the way the
                  //      previous code did.
                  const call = editorial.nationalCall;
                  const useOfficial =
                    typeof call.pl_pct_official === 'number' ||
                    typeof call.pn_pct_official === 'number' ||
                    typeof call.other_pct_official === 'number';
                  if (useOfficial) {
                    const winnerPct =
                      winnerParty === 'PL' ? call.pl_pct_official : call.pn_pct_official;
                    const runnerPct =
                      winnerParty === 'PL' ? call.pn_pct_official : call.pl_pct_official;
                    if (
                      typeof winnerPct !== 'number' ||
                      typeof runnerPct !== 'number'
                    ) {
                      return null;
                    }
                    const margin = winnerPct - runnerPct;
                    return (
                      <p className="hero__winner-margin hero__winner-margin--official">
                        <span className="hero__winner-margin-label">Official margin</span>{' '}
                        <span className="hero__winner-margin-value">
                          {margin > 0 ? '+' : ''}
                          {margin.toFixed(1)} pp
                        </span>
                        <span className="hero__winner-margin-split">
                          {' '}({winnerPct.toFixed(1)}% vs {runnerPct.toFixed(1)}%)
                        </span>
                      </p>
                    );
                  }
                  const pp = call.projected_margin_pct;
                  const votes = call.projected_margin_votes;
                  const hasPp = typeof pp === 'number';
                  const hasVotes = typeof votes === 'number';
                  if (!hasPp && !hasVotes) return null;
                  return (
                    <p className="hero__winner-margin hero__winner-margin--projected">
                      <span className="hero__winner-margin-label">Projected margin</span>{' '}
                      {hasPp && (
                        <span className="hero__winner-margin-value">
                          +{pp.toFixed(1)} pp
                        </span>
                      )}
                      {hasPp && hasVotes && (
                        <span className="hero__winner-margin-split">
                          {' '}(+{votes.toLocaleString()} votes)
                        </span>
                      )}
                      {!hasPp && hasVotes && (
                        <span className="hero__winner-margin-value">
                          +{votes.toLocaleString()} votes
                        </span>
                      )}
                    </p>
                  );
                })()}
              </div>
              {editorial.nationalCall.calledAt && (
                <p className="hero__winner-meta">
                  Called {formatCalledAt(editorial.nationalCall.calledAt)}
                  {editorial.nationalCall.note ? ` · ${editorial.nationalCall.note}` : ''}
                </p>
              )}
            </div>
          ) : showTurnoutHeadline ? (
            <div className="hero__turnout">
              <div className="hero__turnout-figure">
                <p className="hero__turnout-kicker">
                  Turnout · {turnoutKicker}
                </p>
                <p className="hero__turnout-number">
                  {turnoutHeadlinePct.toFixed(1)}
                  <span className="hero__turnout-percent">%</span>
                </p>
                {/* Full-breakdown CTA right under the headline figure
                    so the eye flows number → action → context. */}
                <a className="hero__turnout-link" href="/turnout">
                  Full breakdown →
                </a>
                {/* Delta vs the matching 2022 national baseline. Use
                    the 10pm baseline when a 10pm reading is showing;
                    otherwise 2pm. Mirrors the cube grid's wave-aware
                    comparison so the hero and the section agree on
                    the comparison. */}
                {(() => {
                  const using10pm = turnout.national.pct_10pm !== null;
                  const baseline2022 = using10pm
                    ? TURNOUT_2022_CLOSE_NATIONAL
                    : TURNOUT_2022_2PM_NATIONAL;
                  const delta = turnoutHeadlinePct - baseline2022;
                  const dir = delta > 0.05 ? 'up' : delta < -0.05 ? 'down' : 'flat';
                  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '·';
                  const waveLabel = using10pm ? 'Final' : '2pm';
                  return (
                    <p className={`hero__turnout-delta hero__turnout-delta--${dir}`}>
                      <span aria-hidden="true">{arrow}</span>{' '}
                      {Math.abs(delta).toFixed(1)} pp vs 2022 {waveLabel}{' '}
                      <span className="hero__turnout-delta-baseline">
                        ({baseline2022.toFixed(1)}% in 2022)
                      </span>
                    </p>
                  );
                })()}
              </div>
            </div>
          ) : (
            <dl className="hero__meta">
              {meta.map((m, i) => (
                <div key={`${m.label}-${i}`} className="hero__meta-item">
                  <dt>{m.label}</dt>
                  <dd>{m.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        <aside className="hero__card" aria-label="Election countdown">
          {isPollsOpen ? (
            // Polls-open card has two states: BEFORE close (status
            // "Polls open" + live countdown + "Closes 22:00"
            // subtitle) and AFTER close (just one simple "Polls
            // Closed" line). The previous render kept the "Polls
            // open" status pill + "Closes 22:00" subtitle visible
            // even after expiry, so the card said both "Polls open"
            // and "Polls have closed" at once. Split into a clean
            // post-expiry branch.
            pollsOpenExpired ? (
              <p className="hero__card-status">
                <span className="hero__card-status-dot" aria-hidden="true" />
                Polls Closed
              </p>
            ) : (
              <>
                <p className="hero__card-status">
                  <span className="hero__card-status-dot" aria-hidden="true" />
                  Polls open
                </p>
                <p className="hero__card-counter">
                  <span className="hero__card-number">{pollsOpenTimerPrimary}</span>
                  <span className="hero__card-unit">{pollsOpenTimerUnit}</span>
                </p>
                <p className="hero__card-secondary">Closes 22:00</p>
              </>
            )
          ) : isPostClose ? (
            // Post-close card mirrors the polls_open card's hierarchy
            // (status pill → big primary moment → one supporting
            // line) so the hero reads as a paired live statement
            // across the two phases. For results_pending the primary
            // is "09:00" + "sorting starts"; for counting the
            // primary is the status itself (no future hour to flag).
            <>
              <p className="hero__card-status">
                <span className="hero__card-status-dot" aria-hidden="true" />
                {postCloseStatusLabel}
              </p>
              {/* All post-close phases (results_pending + counting +
                  counting_over + official_results) follow the same
                  card shape: status pill at the top + one supporting
                  line below. post_election deliberately drops the
                  large secondary unit so the card reads as a small
                  archival chip — the nationalCall headline ("Partit
                  Laburista wins") is the loud thing on this phase,
                  not the card. Other post-close phases (results_pending,
                  counting, counting_over, official_results) keep the
                  big secondary line ("Naxxar Counting Hall", "Live
                  updates as the count progresses", etc). */}
              {!isPostElection && (
                <p className="hero__card-counter hero__card-counter--text">
                  <span className="hero__card-unit">{postCloseStatusSecondary}</span>
                </p>
              )}
            </>
          ) : (
            <>
              <p className="hero__card-label">{countdownLabel}</p>
              <p className="hero__card-counter">
                <span className="hero__card-number">{countdownNumber}</span>
                <span className="hero__card-unit">{countdownUnit}</span>
              </p>
            </>
          )}
          {/* Date + note appear only on the `normal` phase. Polls-day
              cards (polls_open / results_pending / counting) all
              fold their context into the single secondary line above
              so the card reads as one statement, not four.
              post_election also drops them — the card stays a small
              archival chip. */}
          {!isAnyLive && !isPostElection && (
            <>
              <p className="hero__card-date">{countdownDate}</p>
              <p className="hero__card-note">{countdownNote}</p>
            </>
          )}
        </aside>

        {/* Live hunch.mt main poll — third hero column. Mounted only
            in `normal` phase; election-day phases (polls_open,
            results_pending, counting) all drop the widget so the
            hero reads as a two-column live statement — countdown /
            status card slides into the rightmost slot. The
            `hero_hunch` flag still gates the polls_open band mount
            via the same FeatureSection key elsewhere. post_election
            also drops it — archive view has no place for a poll. */}
        {!isAnyLive && !isPostElection && (
          <FeatureSection sectionKey="hero_hunch" label="Hero — Hunch.mt widget">
            <div className="hero__hunch hunch-embed--hero">
              <HunchMainEmbed />
            </div>
          </FeatureSection>
        )}
      </div>
    </section>
  );
}

// ── helpers ──────────────────────────────────────────────────────

function formatCalledAt(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return iso;
  }
}

